<template>
	<li v-if="field.state === 3" class="draggable-item" :data-id="field.property.id">
		<div class="item-content">

			<div>
				<div>
					<div class="handle">
						<div class="grip"></div>
					</div>
				</div>

				<div>
					<input type="text" v-model="field.property.translations.en.value" placeholder="Property name" :class="{ 'error' : field.error }">
				</div>
			</div>

			<div>
				<div class="width-300">
					<div class="selector">
						<select v-model="field.property.settings.type.value" @change="typeChangedHandler" :data-field="field.property.id">
							<option v-if="data.children.length === 0">Select type</option>

							<optgroup label="Text" v-if="data.children.length === 0">
								<option value="Textfield" selected>Textfield</option>
								<option value="Number">Number</option>
								<option value="Decimal">Decimal</option>
								<option value="Date">Date</option>
								<option value="E-mail">E-mail</option>
								<option value="Password">Password</option>
							</optgroup>

							<optgroup label="Long text" v-if="data.children.length === 0">
								<option value="Textarea">Textarea</option>
								<option value="Editor">Editor</option>
							</optgroup>

							<optgroup label="Options">
								<option value="Select">Select</option>
								<option value="Multiselect">Multiselect</option>
								<option value="Radio">Radio</option>
								<option value="Checkbox">Checkbox</option>
								<option value="Toggle">Toggle</option>
							</optgroup>

							<optgroup label="Files" v-if="data.children.length === 0">
								<option value="Fileupload">Fileupload</option>
								<option value="Dropzone">Dropzone</option>
							</optgroup>

							<optgroup label="Specials">
								<option value="Reference">Reference</option>
								<option value="Editable reference" v-if="data.children.length === 0">Editable reference</option>
								<option value="Link" v-if="data.children.length === 0">Link (to)</option>
								<option value="Hidden" v-if="data.children.length === 0">Hidden</option>
							</optgroup>

							<optgroup label="Showpad" v-if="data.children.length === 0">
								<option value="Tag">Tag</option>
								<option value="Link">Link (to)</option>
							</optgroup>
						</select>
					</div>
				</div>
				<div class="width-100" v-if="addressable">
					<template v-if="field.property.settings.type.value === 'Textfield'">
						<div></div>
						<input type="radio" name="referenceDisplayProperty" :checked="field.referenceDisplay" :data-field-id="field.property.id" v-on:click="changeReferenceDisplayProperty">
					</template>
				</div>
				<div class="width-150">
					<input type="checkbox" class="toggle" v-model="field.property.settings.mandatory.value">
				</div>
				<div class="width-150">
					<input type="checkbox" class="toggle" v-model="field.property.settings.synced.value" @change="syncedChangeHandler">
				</div>
				<div class="width-150">
					<template v-if="field.property.settings.maxvalues">
						<input
						v-if="['Reference', 'Editable reference', 'Dropzone', 'Multiselect', 'Checkbox'].indexOf(field.property.settings.type.value) > -1"
						type="number"
						min="0"
						v-model="field.property.settings['maxvalues']['value']">
					</template>
				</div>
				<div class="width-150">
					<input type="number" min="0" v-model="field.property.settings.orderinoverview.value">
				</div>

				<div class="width-150" v-if="mainSlug === 'submissions'">
					<input type="checkbox" class="toggle" v-model="field.property.settings.editablesubmission.value">
				</div>

				<div class="btns">
					<button class="warning" :class="{ 'visible' : warningMessage }" :data-message="warningMessage" @mouseenter="warningMouseenterHandler" @mouseleave="warningMouseleaveHandler"><i class="fa fa-exclamation-triangle"></i></button>
					<button :data-field-id="field.property.id" v-on:click="openSidebarClickHandler"><i class="fa fa-cog"></i></button>
					<button class="btn-delete-confirm fa fa-trash-alt" :id="field.property.id" :disabled="data.children.length > 0"></button>
				</div>
			</div>

		</div>

		<!-- A field can only have childs when the field type is a type of option (checkbox, radio, select) -->
		<ul class="draggable-container"
			v-if="['Select', 'Multiselect', 'Radio', 'Checkbox', 'Toggle', 'Reference'].indexOf(field.property.settings.type.value) > -1 && hasOptions === true
			|| field.property.settings.type.value === 'Toggle' && field.id !== 0">
			<Field v-for="(child, index) in data.children"
				:key="index"
				:data="child"
				:entity="entity"
				:fields="fields"
				:openSidebarCallback="openSidebarClickHandler"
				:typeChangedHandlerCallback="typeChangedHandler"
				:changeReferenceDisplayPropertyCallback="changeReferenceDisplayProperty"
				:setFieldSynced="setFieldSynced"
				ref="fieldElement"
			></Field>
		</ul>
	</li>
</template>

<script>

import general from '../../modules/general.js'

export default {
	name: 'Field',
	props: {
		data: Object,
		fields: Object,
		openSidebarCallback: Function,
		typeChangedHandlerCallback: Function,
		changeReferenceDisplayPropertyCallback: Function,
		setFieldSynced: Function,
		entity: Object
	},

	data () {
		return {
			field: null,
			hasOptions: false,
			addressable: false,
			mainSlug: general.getMainSlug()
		}
	},

	created () {
		this.findCorrectField()
		if (this.entity.settings.addressable) {
			if (this.entity.settings.addressable.value == 'true') this.addressable = true
		}
	},

	updated () {
		this.findCorrectField()
		this.syncedChangeHandler()
	},

	mounted () {
	},

	computed: {
		warningMessage: function () {
			// Show a warning in 2 cases
			// - When an options type field does not have any defined options
			// - when a conditional child does not have a conditional value
			var message
			if (!this.hasOptions && ['Select', 'Multiselect', 'Radio', 'Checkbox'].includes(this.field.property.settings.type.value)) {
				message = 'Please add some options for this field.'
			}

			if (this.field.property.settings.conditional && this.field.property.settings.conditional.state !== 5) {
				var emptyConditionalValue = this.field.property.settings.conditional.value === '[]'
				var matchWithParent = false

				// detect if the present value is an option for the chosen parent
				if (!emptyConditionalValue) {
					var chosenConditionalValues = JSON.parse(this.field.property.settings.conditional.value)
					var parentpropertyID = this.field.property.settings.conditionalParent.value
					loop1:
					for (var key in this.fields) {
						if (this.fields[key].property.id == parentpropertyID) {
							if (this.fields[key].property.settings.type.value === 'Reference') {
								matchWithParent = true
								continue
							}

							var parentOptions = this.fields[key].options
							var parentFieldType = this.fields[key].property.settings.type.value
							if (parentFieldType === 'Toggle') {
								matchWithParent = true
								continue
							}
							loop2:
							for (var opt in parentOptions) {
								var val = parentOptions[opt].value
								if (chosenConditionalValues.includes(val)) {
									matchWithParent = true
									break loop1
								}
							}
						}
					}
				}

				if (emptyConditionalValue || !matchWithParent) {
					message = 'Please select the conditional values for this field.'
				}
			}

			return message
		}
	},

	methods: {
		findCorrectField () {
			// find the correct field for the given field ID
			var ID = this.data.fieldPropertyID
			for (var key in this.fields) {
				if (ID === this.fields[key].property.id) {
					this.field = this.fields[key]

					// create empty translation in case it was lost in the DB in order not to break the interface :/
					if (!this.field.property.translations) {
						this.field.property['translations'] = {
							'en': {
								'id': 0,
								'market': 1,
								'state': 3,
								'value': ''
							}
						}
					}

					// create empty setting type in case it was lost in the DB in order not to break the interface :/
					if (!this.field.property.settings) {
						this.field.property['settings'] = {
							'type': {
								'id': 0,
								'name': 'type',
								'state': 3,
								'value': ''
							},
							'mandatory': {
								'id': 0,
								'name': 'mandatory',
								'state': 3,
								'value': ''
							},
							'synced': {
								'id': 0,
								'name': 'synced',
								'state': 3,
								'value': ''
							},
							'orderinoverview': {
								'id': 0,
								'name': 'orderinoverview',
								'state': 3,
								'value': '0'
							}
						}
					}

					// add editablesubmission setting
					if (!this.field.property.settings['editablesubmission'] && this.mainSlug === 'submissions') {
						this.field.property.settings['editablesubmission'] = {
							'id': 0,
							'name': 'editablesubmission',
							'state': 3,
							'value': ''
						}
					}

					if (this.field.options) {
						if (Object.keys(this.field.options).length > 0) this.hasOptions = true
					} else if (this.field.property.settings) {
						if (this.field.property.settings.reference) {
							if (this.field.property.settings.reference.value) this.hasOptions = true
						}
					}
					break
				}
			}
		},

		changeReferenceDisplayProperty (e) {
			this.changeReferenceDisplayPropertyCallback(e)
		},

		typeChangedHandler (e) {
			e.preventDefault()
			this.typeChangedHandlerCallback(e)
		},

		openSidebarClickHandler (e) {
			e.preventDefault()
			this.openSidebarCallback(e)
		},

		warningMouseenterHandler (e) {
			var target = e.currentTarget
			target.classList.add('open')
		},

		warningMouseleaveHandler (e) {
			var target = e.currentTarget
			target.classList.remove('open')
		},

		syncedChangeHandler () {
			var value = this.field.property.settings.synced.value
			if (value) {
				// if this field is a conditional child then we have to make the parent synced as well
				if (this.field.property.settings.conditionalParent) {
					this.setFieldSynced(this.field.property.settings.conditionalParent.value)
				}
			}
		},

		rerender () {
			this.$forceUpdate()
			this.updateAllFields()
		},

		updateAllFields () {
			if (this.$refs.fieldElement) {
				for (var i = this.$refs.fieldElement.length - 1; i >= 0; i--) {
					this.$refs.fieldElement[i].rerender()
				}
			}
		}
	}
}

</script>
